import {configureStore, combineReducers, DevToolsEnhancerOptions, Middleware} from '@reduxjs/toolkit'
import {v4} from 'uuid'

import {createLogger} from 'redux-logger'
import createSagaMiddleware from 'redux-saga'

import {API_URL, loadSessionStorage} from '../lib'
import {baseRequest} from '../lib/request'
import {REFRESH_REQUEST_TIMEOUT_MS} from '../lib/auth/refresh_token_action'
import {
  isLogoutPending,
  logoutPendingGeneration,
  logoutPendingValue,
  recordLogoutReply,
  setLogoutPending,
  trackBootLogout,
} from '../lib/auth/logout_pending'
import {DeployStage, Env} from '../env'
import * as stateInterface from './state'
import * as sagaInterface from './saga'
import {redactForLogging} from './redact_for_logging'
import Instrumentation from '../instrumentation'

// Session storage only ever holds a subset of the `auth` and `session` slices, so on every boot
// -- signed in or not -- the slice defaults are spread UNDER whatever was persisted. A field
// added later (`refreshUnavailable` today) then hydrates with its default instead of `undefined`.
const sanitizePreloadedState = (preloadedState: any) => {
  const {refreshToken: _refreshToken, ...persistedAuth} = preloadedState?.auth ?? {}

  return {
    ...preloadedState,
    auth: {
      ...stateInterface.authDefaultState,
      ...persistedAuth,
    },
    session: {
      ...stateInterface.sessionStateDefaultState,
      ...(preloadedState?.session ?? {}),
    },
  }
}

const storeInterface: {[key: string]: any} = {
  ...sagaInterface,
  ...stateInterface,
}

// combine all functions in src/store/{state,saga} that end en Reduce into one Object
// keys will be function name without the 'Reducer' suffix
// for example the 'themeReducer' function well be come a key called 'theme'
const reducer = combineReducers(
  Object.keys(storeInterface).reduce((result, key) => {
    const current = key.endsWith('Reducer')
      ? ({[key.replace('Reducer', '')]: storeInterface[key]} as {[key: string]: any})
      : {}
    return {
      ...current,
      ...result,
    }
  }, {}),
)

// export type RootState = ReturnType<typeof reducer>

const sagaFunctionList = Object.keys(storeInterface).reduce((result: any, key) => {
  const current = key.endsWith('Saga') ? [storeInterface[key]] : []
  return [...current, ...result]
}, [])

// The console logger and the DevTools bridge are attached on every stage below production -- test,
// develop and staging keep their logs -- and on production neither is attached, so no dispatched
// action (the login password among them) and no state snapshot can reach a real user's console or
// a browser extension. Production is the topmost stage, so "below production" is
// simply "not production", and taking the stage as an argument keeps the rule testable.
export const loggingEnabledForStage = (stage: DeployStage): boolean => stage !== DeployStage.Production

// the boot retry of a logout the server never confirmed. It goes straight at `baseRequest`
// rather than through `clearIdentitySession`, whose `finally` wipes `labfront` and could therefore
// race a login the user starts in this same tab. Best effort: the marker is all that has to
// survive, and it only goes on the server's definitive answer (a 2xx, 401 or 404, see
// `isDefinitiveLogoutReply`).
const sendBootLogout = (signal: AbortSignal): Promise<void> => {
  // this reply can land long after the boot: the marker exists *because* a previous logout hung,
  // so the retry is likely to hang too. Every write is skipped once the reply no longer speaks for
  // the marker this boot found:
  // - a login in this tab abandoned the request at the 3s cap (`waitForBootLogout`). That is no
  //   answer at all, and rewriting the marker would end the session of every sibling tab.
  // - the generation moved: a late failure must not re-arm the marker over a login in this tab.
  // - the stored value changed: a login in another tab removes the shared marker without moving
  //   the generation, and a sign-out there replaces it with a newer one.
  const generation = logoutPendingGeneration()
  const bootMarker = logoutPendingValue()
  const speaksForThisBoot = () =>
    !signal.aborted && logoutPendingGeneration() === generation && logoutPendingValue() === bootMarker

  return baseRequest({
    method: 'post',
    url: `${API_URL}/v1/web/identity-logout`,
    timeout: REFRESH_REQUEST_TIMEOUT_MS,
    signal,
  })
    .then((result) => {
      if (speaksForThisBoot()) recordLogoutReply(result?.statusCode)
    })
    .catch(() => {
      if (speaksForThisBoot()) setLogoutPending()
    })
}

export const createAppStore = (loggingEnabled: boolean = loggingEnabledForStage(Env.shared.deployStage)) => {
  const saga = createSagaMiddleware({
    onError: (error, info) => {
      Instrumentation.shared.perform((api) => {
        const state = store.getState() as any
        if (state?.profile?.id) {
          const {id, email, firstName, lastName} = state.profile
          api.setUser({
            id,
            email,
            username: `${firstName} ${lastName}`,
          })
        }
        api.pushError(error, {
          context: {
            info: JSON.stringify(info),
            source: 'Saga',
          },
        })
      })
      throw error
    },
  })
  const preloadedState: any = sanitizePreloadedState(loadSessionStorage() ? loadSessionStorage() : {})
  if (preloadedState?.totalParticipants) {
    delete preloadedState.totalParticipants
    delete preloadedState.currentWorkspaceId
  }
  const hasAccessToken = Boolean(preloadedState?.auth?.accessToken)
  // a tab that boots with the marker set retries the logout instead of refreshing. `auth`
  // stays idle -- no `isRefreshing`, no refresh dispatch -- so `ProtectedRoute` shows the login
  // page while the retry goes out, and a still-live cookie cannot sign the user back in.
  // This branch lives here rather than in a saga because `createTestStore` runs every `*Saga`,
  // so a boot saga would fire in every test.
  const logoutPending = isLogoutPending()
  const shouldBootRefresh = !hasAccessToken && !logoutPending
  if (logoutPending) {
    // The marker is shared across tabs (`localStorage`) while the token is this tab's own
    // (`sessionStorage`), so a sibling tab reloading after another tab's unconfirmed logout boots
    // holding both. Dropping the persisted `auth` is what makes the paragraph above true: with the
    // token left in place `ProtectedRoute` renders the app instead of the login page, and since no
    // refresh cycle starts, the coordinator's `isLogoutPending()` gate never runs either.
    preloadedState.auth = {...stateInterface.authDefaultState}
  }
  if (shouldBootRefresh) {
    preloadedState.auth = {
      ...stateInterface.authDefaultState,
      ...preloadedState.auth,
      isRefreshing: true,
    }
  }
  const store = configureStore({
    reducer,
    middleware: (getDefaultMiddleware) => {
      const middleware = getDefaultMiddleware({
        serializableCheck: {
          ignoredActionPaths: ['setRequestResult'],
        },
      }).concat(saga as Middleware)

      if (!loggingEnabled) {
        return middleware
      }

      // Where the logger stays on, the action and both state snapshots are redacted before they
      // are printed, so a proof value never reaches a develop or staging console either.
      const logger = createLogger({
        actionTransformer: redactForLogging,
        stateTransformer: redactForLogging,
      })
      return middleware.concat(logger as Middleware)
    },
    // The DevTools bridge gets the same redactor as the console logger beside it. A bare `true`
    // hands the browser extension the unredacted action -- `currentPassword` among them -- on
    // exactly the stages where the confirm step is switched on.
    devTools: loggingEnabled && {
      actionSanitizer: redactForLogging as NonNullable<DevToolsEnhancerOptions['actionSanitizer']>,
      stateSanitizer: redactForLogging as NonNullable<DevToolsEnhancerOptions['stateSanitizer']>,
    },
    preloadedState,
  })
  sagaFunctionList.forEach(saga.run)
  // the login sagas wait on this, so an ordinary boot has to put the record back to empty
  if (logoutPending) {
    const controller = new AbortController()
    trackBootLogout(sendBootLogout(controller.signal), controller)
  } else {
    trackBootLogout(null)
  }
  if (shouldBootRefresh) {
    store.dispatch(
      sagaInterface.doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT({
        requestId: v4(),
      }),
    )
  }
  return store
}

export const createTestStore = (initialState?: any) => {
  const logger = createLogger()
  const preloadedState: any = sanitizePreloadedState(loadSessionStorage() ? loadSessionStorage() : {})
  const saga = createSagaMiddleware()
  const store = configureStore({
    reducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActionPaths: ['setRequestResult'],
        },
      }).concat(saga as Middleware),
    preloadedState: {...preloadedState, ...initialState},
  })
  sagaFunctionList.forEach(saga.run)

  return store
}
